/**
 * MoreButton — per-row action trigger. Lives in the pinned trailing
 * column and opens the context menu with actions supplied by the
 * page via `actions({ row, setAnchorEl })`.
 *
 * Logic is unchanged vs. legacy: the same event-stopping handlers
 * (so the outer row's onClick doesn't fire through), the same MUI
 * `Menu` primitive for the popover, the same skeleton shimmer while
 * rows are loading. Visual polish: muted icon colour that picks up
 * the brand on hover, no border radius on the hit-target so the row
 * highlight still reads as one surface.
 */
import { memo, useState } from 'react';
import { IconButton, Menu } from 'mui/core';
import { styled } from '@mui/material/styles';
import { ReactComponent as MenuIcon } from 'assets/icons/expand_circle_down.svg';

const PREFIX = 'MoreButton';
const classes = {
  shimmer: `${PREFIX}-shimmer`,
  button: `${PREFIX}-button`,
  trigger: `${PREFIX}-trigger`,
};

const Container = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',

  // Background + animation come from the global `.qhs-shimmer`
  // utility (see `tokens.css`), so this rule only handles layout.
  // Was `var(--qhs-surface-hover)` which on dark theme sat almost
  // identical to the row surface and read as "nothing here" rather
  // than "loading". The shared utility uses the dedicated skeleton
  // token + gradient sweep that works in both palettes.
  [`& .${classes.shimmer}`]: {
    maxHeight: 20,
    position: 'absolute',
    display: 'flex',
    width: 30,
    height: 34,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
  [`& .${classes.button}`]: {
    minHeight: '100%',
    width: '100%',
    height: '100%',
    position: 'absolute',
    inset: 0,
  },
  [`& .${classes.trigger}`]: {
    width: '100%',
    height: '100%',
    borderRadius: 0,
    color: 'var(--qhs-text-2)',
    transition: 'color 120ms ease, background-color 120ms ease',
    '&:hover': {
      color: 'var(--qhs-brand)',
      background: 'var(--qhs-surface-hover)',
    },
    '& svg': { width: 18, height: 18 },
  },
});

const MoreButton = memo(function MoreButton({ row, actions, canShowSkeletons }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = event => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setAnchorEl(null);
  };

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <Container className="more-button">
      {row.original.showShimmer || canShowSkeletons ? (
        <div className={`${classes.shimmer} qhs-shimmer`} />
      ) : (
        <div className={classes.button}>
          <IconButton
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            size="small"
            className={classes.trigger}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            // Token-driven paper so the row-actions popover follows
            // theme — was a hardcoded white MUI default that read as
            // a glaring white card on dark backgrounds. MUI v9 routes
            // paper styling through `slotProps.paper` (the legacy
            // `PaperProps` prop is dropped), so we set it there.
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: 'var(--qhs-surface)',
                  color: 'var(--qhs-text-1)',
                  border: '1px solid var(--qhs-border)',
                  boxShadow:
                    '0 8px 24px rgba(26, 45, 138, 0.08), 0 24px 60px rgba(26, 45, 138, 0.12)',
                },
              },
            }}
          >
            {actions && actions({ row, setAnchorEl })}
          </Menu>
        </div>
      )}
    </Container>
  );
});

export default MoreButton;
